import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { GenericError, ProgressBar, OtpInput, Alert, AlertType } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useEffect, useState, type FC } from 'react'
import {
  useGetEnrollmentMultiVerifyEmailData,
  useSubmitEnrollmentMultiResendEmailData,
  useSubmitEnrollmentMultiVerifyEmailData,
} from '@egw/api'
import { useNavigate } from 'react-router'
import { useTranslation } from '@egw/i18n'
import { CheckIcon } from '@heroicons/react/24/outline'
import { passkeyService } from '@egw/auth'
import { PageHeader } from '../../../components'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as MULTI_ROUTES } from '../../../constants/routes/routes-multi'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'

const log = new Logger('Enrollment - Multi - VerifyEmail')

export const VerifyEmailPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const isMobile = passkeyService.getIsMobile()

  const [isInvalidToken, setIsInvalidToken] = useState(false)
  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)
  const [hasShownIntent, setHasShownIntent] = useState(false)
  const [otpValue, setOtpValue] = useState('')

  const { data, isLoading, error } = useGetEnrollmentMultiVerifyEmailData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiVerifyEmailData()
  const {
    mutate: resend,
    isLoading: isResendSubmitting,
    error: submitResendError,
  } = useSubmitEnrollmentMultiResendEmailData()

  const email = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.email : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''
  const progress = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.progress : undefined

  useEffect(() => {
    if (!hasShownIntent && otpValue) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_VERIFYEMAIL_INTENT, {
        applicationId,
      })
    }
  }, [applicationId, otpValue, hasShownIntent])

  // Protect route
  useValidateRoute(data?.path)

  const handleOtpComplete = (otp: string): void => {
    void onSubmit(otp)
  }

  const handleOtpChange = (value: string): void => {
    const trimmedValue = value.slice(0, 6)
    setOtpValue(trimmedValue)
  }

  const onSubmit = async (emailOtpCode: string): Promise<void> => {
    setIsInvalidToken(false)
    try {
      const response = await mutate({
        emailOtpCode,
      })
      // Handle different error types
      if (response?.__typename === 'SubmitEnrollmentError') {
        if (response.type === 'INVALID_TOKEN') {
          setIsInvalidToken(true)
          return
        }

        if (response.type === 'APPLICANT_IN_MANUAL_REVIEW' && response.path) {
          void navigate(response.path)
        }

        if (response.type === 'USER_ALREADY_EXISTS' && response.path) {
          void navigate(response.path)
        }
      }
      // If path exists and is different
      if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
        if (response.path.includes(MULTI_ROUTES.MULTI_VERIFY_PHONE.path)) {
          // navigate(response.path)
          if (!isMobile) {
            await mutate({
              triggerPhoneOtp: true,
            })
          }
          navigate(response.path)
        }
      } else {
        // If no path from BE, redirect to error
        if (!response?.path) {
          navigate(`${WEALTH_ROUTES.ENROLLMENT}/${GENERIC_ROUTES.ERROR}`)
        }
      }
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  const resendEmail = (): void => {
    setIsInvalidToken(false)
    setCodeResent(true)

    sendEvent(EDataLayerEventWealth.BANKHYCHYS_EMAILOTPRESEND_SUBMIT, {
      applicationId,
    })

    setTimeout(() => {
      void sendMagicLinkEmail()
      setCounter(30)
      const intervalId = setInterval(() => {
        setCounter((prev) => {
          if (prev <= 1) {
            clearInterval(intervalId)
            setCodeResent(false)

            return 0
          }
          return prev - 1
        })
      }, 1000)
    }, 5000)
  }

  const sendMagicLinkEmail = async (): Promise<void> => {
    try {
      await resend({
        triggerEmailOtp: true,
      })
    } catch (e) {
      log.error('Resend email error', e)
    }
  }

  return (
    <EnrollmentMultiLayout title={t('multi.verify_email.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <div className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t('multi.verify_email.title')} />
          <ProgressBar showAnimation progressPercentage={progress} className="mb-xl" />
          <p className="typo-primary-base-regular text-text-body mb-3xs">{t('multi.verify_email.description')}</p>
          <p className="typo-primary-base-semibold text-text-header mb-md fs-exclude">{email}</p>
          <p className="typo-primary-base-regular text-text-meta mb-md">{t('multi.verify_email.check_your_email')}</p>
          <OtpInput
            value={otpValue}
            onChange={handleOtpChange}
            onComplete={handleOtpComplete}
            testId="otp-input"
            error={isInvalidToken ? t('multi.verify_email.code_expired') : ''}
          />

          <div className="flex items-baseline gap-4">
            <button
              onClick={resendEmail}
              disabled={isResendSubmitting || codeResent || isSubmitting}
              className={`typo-primary-base-semibold text-left mt-sm w-fit ${codeResent ? 'text-gray-400' : ' text-text-action'}`}
              type="button"
              data-testid="sendEmailOtp_button"
            >
              {t('multi.verify_email.resend_email')}
            </button>
            {codeResent && !counter ? (
              <p className="typo-primary-base-semibold text-text-header-success flex items-center gap-2">
                {t('multi.verify_email.code_sent')} <CheckIcon className="size-5" />
              </p>
            ) : null}
            {counter ? (
              <p className="typo-primary-base-semibold text-text-meta mt-2xs">
                {t('multi.verify_email.send_another_code', {
                  time: counter,
                })}
              </p>
            ) : null}
          </div>

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}
        </div>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {(error ?? submitResendError) ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
